/**
 * The frame for reading pages (About, Terms, Privacy, API and MCP): one column at the prose
 * measure that every block shares, so text, lists, code and tables end on the same edge, and an
 * "On this page" rail beside it on wide screens.
 *
 * The rail lists the article's own <h2 id> sections, read from its children while rendering, so
 * it is in the prerendered HTML too and needs no effect. Pages whose sections arrive later can
 * add entries through `toc`.
 */
import { Children, isValidElement, type ReactElement, type ReactNode } from 'react'

export interface TocEntry {
  id: string
  label: string
  children?: TocEntry[]
}

type Props = { id?: string; children?: ReactNode }

/** The `<h2 id>` headings among the article's children, and those one level inside a `<section>`. */
function sectionsOf(children: ReactNode): TocEntry[] {
  const out: TocEntry[] = []
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return
    const el = child as ReactElement<Props>
    if (el.type === 'h2' && el.props.id && typeof el.props.children === 'string')
      out.push({ id: el.props.id, label: el.props.children })
    else if (el.type === 'section') out.push(...sectionsOf(el.props.children))
  })
  return out
}

export function ReadingPage({
  className,
  toc = [],
  children,
}: {
  className?: string
  /** Entries added after the article's own sections, or children for one of them by id. */
  toc?: TocEntry[]
  children: ReactNode
}) {
  const entries: TocEntry[] = sectionsOf(children).map((s) => ({
    ...s,
    children: toc.find((t) => t.id === s.id)?.children,
  }))
  entries.push(...toc.filter((t) => !entries.some((e) => e.id === t.id)))
  return (
    <div className={`page reading-page${className ? ` ${className}` : ''}`}>
      <article className="reading">{children}</article>
      {entries.length > 2 && (
        <nav className="reading-toc" aria-label="On this page">
          <p className="reading-toc-label">On this page</p>
          <TocList entries={entries} />
        </nav>
      )}
    </div>
  )
}

function TocList({ entries }: { entries: TocEntry[] }) {
  return (
    <ol>
      {entries.map((e) => (
        <li key={e.id}>
          <a href={`#${e.id}`}>{e.label}</a>
          {e.children && e.children.length > 0 && <TocList entries={e.children} />}
        </li>
      ))}
    </ol>
  )
}
